import * as THREE from 'three'
import { serializeFrustum } from './frustum'
import { CHUNK_DEPTH, CHUNK_WIDTH, REGION_SIZE } from './chunks'

const TICK_RATE = 1 / 10 // 5Hz

const v1 = new THREE.Vector3()

const cameraXZ = []

const frustum = new THREE.Frustum()
const frustumProjMat = new THREE.Matrix4()
const frustumData = []

export function createTerrainManager({ scene, material, camera, registry, onBuild }) {
  const chunks = new Map() // id -> chunk
  const regions = new RegionManager(scene, material)

  const worker = new Worker(window.TERRAIN_WORKER_PATH)

  worker.onmessage = e => {
    // console.time('chunk-builder-receive')
    const msgs = e.data.op === 'batch' ? e.data.msgs : [e.data]
    for (const msg of msgs) {
      if (msg.op === 'update_collider') {
        chunks.get(msg.chunkId)?.onCollider(msg.collider)
      }
      if (msg.op === 'upsert_region') {
        regions.upsertRegion(msg.regionId, msg.geometry)
        for (const chunkId of msg.chunkIds) {
          onBuild(chunkId)
        }
      }
      if (msg.op === 'remove_region') {
        regions.destroyRegion(msg.regionId)
      }
    }
    // console.timeEnd('chunk-builder-receive')
  }

  worker.onerror = err => {
    console.error(err)
    console.log('chunk builder worker error')
  }

  worker.postMessage({
    op: 'init',
    registry: registry.serialize(),
  })

  registry.on('change', change => {
    worker.postMessage({
      op: 'registry_change',
      change,
    })
  })

  function register(chunk) {
    // if (chunks.has(chunk.id)) {
    //   throw new Error(`chunk builder received add for chunk (${chunk.id}) already added`)
    // }
    chunks.set(chunk.id, chunk)
    const onCollider = chunk.onCollider
    chunk.onCollider = null
    worker.postMessage({
      op: 'add',
      chunk,
    })
    chunk.onCollider = onCollider
    return {
      // edit() ????
      destroy() {
        chunk.onCollider = null
        chunks.delete(chunk.id)
        worker.postMessage({
          op: 'destroy',
          id: chunk.id,
        })
      },
    }
  }

  let lastTick = 0
  function update(delta) {
    lastTick += delta
    if (lastTick > TICK_RATE) {
      lastTick -= TICK_RATE
      // camera.updateMatrixWorld(true)
      camera.getWorldPosition(v1).setY(0).toArray(cameraXZ)
      frustumProjMat.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
      frustum.setFromProjectionMatrix(frustumProjMat)
      serializeFrustum(frustum, frustumData)
      worker.postMessage({
        op: 'tick',
        cameraXZ,
        frustumData,
      })
    }
  }

  function edit(changes) {
    worker.postMessage({
      op: 'edit',
      changes,
    })
  }

  return {
    register,
    update,
    edit,
  }
}

// Main thread - RegionManager.js

class RegionManager {
  constructor(scene, material) {
    this.scene = scene
    this.material = material
    this.regions = new Map() // regionId -> { mesh, collider }
  }

  upsertRegion(regionId, geometryData) {
    let region = this.regions.get(regionId)

    if (!region) {
      region = {
        mesh: null,
        collider: null,
      }
      this.regions.set(regionId, region)
    }

    // Update geometry/mesh
    if (geometryData) {
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute('position', new THREE.BufferAttribute(geometryData.position, 3))
      geometry.setIndex(new THREE.BufferAttribute(geometryData.index, 1))
      geometry.setAttribute('tileId', new THREE.BufferAttribute(geometryData.tileId, 1))
      geometry.setAttribute('uv', new THREE.BufferAttribute(geometryData.uv, 2))
      geometry.setAttribute('light', new THREE.BufferAttribute(geometryData.light, 4))
      geometry.setAttribute('ao', new THREE.BufferAttribute(geometryData.ao, 1))
      geometry.setAttribute('dim', new THREE.BufferAttribute(geometryData.dim, 1))
      geometry.setAttribute('wind', new THREE.BufferAttribute(geometryData.wind, 1))

      if (region.mesh) {
        region.mesh.geometry.dispose()
        region.mesh.geometry = geometry
      } else {
        region.mesh = new THREE.Mesh(geometry, this.material)
        region.mesh.position.copy(getRegionWorldPosition(regionId))
        this.scene.add(region.mesh)
      }
    } else if (region.mesh) {
      // No geometry, remove mesh
      this.scene.remove(region.mesh)
      region.mesh.geometry.dispose()
      region.mesh = null
    }
  }

  destroyRegion(regionId) {
    const region = this.regions.get(regionId)
    if (region) {
      if (region.mesh) {
        this.scene.remove(region.mesh)
        region.mesh.geometry.dispose()
      }
      this.regions.delete(regionId)
    }
  }

  destroy() {
    for (const regionId of this.regions.keys()) {
      this.destroyRegion(regionId)
    }
  }
}

function parseRegionId(regionId) {
  const [rx, rz] = regionId.split('_').map(Number)
  return { rx, rz }
}

function getRegionWorldPosition(regionId) {
  const { rx, rz } = parseRegionId(regionId)
  const ox = rx * REGION_SIZE
  const oz = rz * REGION_SIZE
  return {
    x: ox * CHUNK_WIDTH,
    y: 0,
    z: oz * CHUNK_DEPTH,
  }
}
